import React from 'react';
import { Chip } from '@mui/joy';

interface MeetingStatusChipProps {
  meetings: any[];
}

const MeetingStatusChip: React.FC<MeetingStatusChipProps> = ({ meetings }) => {
  // Helper function to get meeting status and date
  const getMeetingStatus = (meetings: any[]) => {
    if (!meetings || meetings.length === 0) return null;
    
    const now = new Date();
    const upcomingMeetings = meetings.filter(meeting => 
      new Date(meeting.scheduledAt) > now
    );
    const pastMeetings = meetings.filter(meeting => 
      new Date(meeting.scheduledAt) <= now
    );
    
    if (upcomingMeetings.length > 0) {
      // Show the earliest upcoming meeting
      const nextMeeting = upcomingMeetings.sort((a, b) => 
        new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
      )[0];
      
      return {
        type: 'upcoming',
        date: new Date(nextMeeting.scheduledAt),
        color: 'success' as const
      };
    } else if (pastMeetings.length > 0) {
      // Show the most recent past meeting
      const lastMeeting = pastMeetings.sort((a, b) => 
        new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime()
      )[0];
      
      return {
        type: 'past',
        date: new Date(lastMeeting.scheduledAt),
        color: 'neutral' as const
      };
    }
    
    return null;
  };

  const formatMeetingDate = (date: Date, isUpcoming: boolean) => {
    const now = new Date();
    
    // Normalize both dates to midnight in browser's timezone for calendar day comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const meetingDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    // Calculate difference in calendar days
    const diffTime = meetingDay.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Tomorrow';
    } else if (diffDays === -1) {
      return 'Yesterday';
    } else if (Math.abs(diffDays) <= 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const meetingStatus = getMeetingStatus(meetings);

  if (!meetingStatus) {
    return null;
  }

  return (
    <Chip
      size="sm"
      color={meetingStatus.color}
      variant="soft"
      sx={{ mb: 1 }}
    >
      Scheduled for {formatMeetingDate(meetingStatus.date, meetingStatus.type === 'upcoming')}
    </Chip>
  );
};

export default MeetingStatusChip; 